import { useEffect, useState, useCallback } from 'react'
import {
  RefreshCw, AlertTriangle, Shield, ChevronRight,
  Database, Activity, CheckCircle2, XCircle, Clock,
  AlertOctagon, Filter,
} from 'lucide-react'
import styles from './BackupStatusPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ReplicationStatus {
  id: number
  product_name: string
  replication_lag_minutes: number
  last_sync_at: string | null
  health: string
  last_backup_at: string | null
  target_rpo_hours: number
  region: string
}

interface BackupAlert {
  id: number
  product_name: string
  alert_type: string
  severity: string
  message: string
  is_active: boolean
  triggered_at: string
}

interface BackupJob {
  id: number
  product_name: string
  backup_type: string
  status: string
  started_at: string
  completed_at: string | null
  size_gb: number | null
  duration_minutes: number | null
}

// ── Color maps ────────────────────────────────────────────────────────────────

const HEALTH_COLORS: Record<string, string> = {
  HEALTHY: '#16A34A',
  WARNING: '#D97706',
  CRITICAL: '#DC2626',
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#DC2626',
  HIGH: '#EA580C',
  MEDIUM: '#D97706',
  LOW: '#6B7280',
}

const JOB_STATUS_COLORS: Record<string, string> = {
  SUCCESS: '#16A34A',
  FAILED: '#DC2626',
  IN_PROGRESS: '#D97706',
  SKIPPED: '#6B7280',
}

// ── Helper components ─────────────────────────────────────────────────────────

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className={styles.badge}
      style={{ '--bc': color } as React.CSSProperties}
    >
      {label}
    </span>
  )
}

function HealthDot({ health }: { health: string }) {
  const color = HEALTH_COLORS[health] ?? '#6B7280'
  return (
    <span className={styles.healthDot} style={{ background: color }} title={health} />
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function lagClass(lagMinutes: number, targetRpoHours: number): string {
  const rpoMinutes = targetRpoHours * 60
  return lagMinutes > rpoMinutes ? styles.lagBad : styles.lagOk
}

// ── Summary card ──────────────────────────────────────────────────────────────

function SummaryCard({
  icon: Icon, label, value, variant,
}: {
  icon: React.ElementType
  label: string
  value: number
  variant: 'ok' | 'warn' | 'bad' | 'neutral'
}) {
  return (
    <div className={`${styles.summaryCard} ${styles[`summaryCard_${variant}`]}`}>
      <div className={styles.summaryIcon}><Icon size={18} /></div>
      <div>
        <div className={styles.summaryValue}>{value}</div>
        <div className={styles.summaryLabel}>{label}</div>
      </div>
    </div>
  )
}

// ── Alert card ────────────────────────────────────────────────────────────────

function AlertCard({ alert }: { alert: BackupAlert }) {
  const color = SEVERITY_COLORS[alert.severity] ?? '#6B7280'
  return (
    <div
      className={styles.alertCard}
      style={{ '--alert-color': color } as React.CSSProperties}
    >
      <div className={styles.alertCardHeader}>
        <AlertOctagon size={14} style={{ color }} />
        <span className={styles.alertProduct}>{alert.product_name}</span>
        <Badge label={alert.severity} color={color} />
        <span className={styles.alertType}>{alert.alert_type.replace(/_/g, ' ')}</span>
        <span className={styles.alertTime}>{formatDate(alert.triggered_at)}</span>
      </div>
      <p className={styles.alertMessage}>{alert.message}</p>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function BackupStatusPage() {
  const [replication, setReplication] = useState<ReplicationStatus[]>([])
  const [alerts, setAlerts] = useState<BackupAlert[]>([])
  const [jobs, setJobs] = useState<BackupJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [healthFilter, setHealthFilter] = useState<string>('ALL')

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const headers = authHeaders()
      const [rRes, aRes, jRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/backup/replication/`, { headers }),
        fetch(`${DR_API}/api/v1/dr/backup/alerts/`, { headers }),
        fetch(`${DR_API}/api/v1/dr/backup/jobs/?page_size=50`, { headers }),
      ])
      const [rData, aData, jData] = await Promise.all([
        rRes.ok ? rRes.json() : [],
        aRes.ok ? aRes.json() : [],
        jRes.ok ? jRes.json() : [],
      ])
      setReplication(Array.isArray(rData) ? rData : (rData.results ?? []))
      setAlerts(Array.isArray(aData) ? aData : (aData.results ?? []))
      setJobs(Array.isArray(jData) ? jData : (jData.results ?? []))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load backup status.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const activeAlerts = alerts.filter(a => a.is_active)
  const healthyCnt = replication.filter(r => r.health === 'HEALTHY').length
  const warningCnt = replication.filter(r => r.health === 'WARNING').length
  const criticalCnt = replication.filter(r => r.health === 'CRITICAL').length

  const filteredReplication = healthFilter === 'ALL'
    ? replication
    : replication.filter(r => r.health === healthFilter)

  return (
    <div className={styles.page}>

      {/* ── Hero ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <Shield size={12} />
            <span>Aptean Platform</span>
            <ChevronRight size={11} />
            <span>Disaster Recovery</span>
            <ChevronRight size={11} />
            <span>Backup Status</span>
          </div>
          <h1 className={styles.heroTitle}>Backup &amp; Replication Status</h1>
          <p className={styles.heroSub}>
            Real-time backup health, replication lag, and job history across all DR-protected products.
          </p>
        </div>
        <div className={styles.heroRight}>
          <button className={styles.btnSecondary} onClick={fetchAll} disabled={loading}>
            <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className={styles.loadingRow}>
          <RefreshCw size={18} className={styles.spin} />
          <span>Loading backup status…</span>
        </div>
      )}

      {!loading && (
        <>
          {/* ── Summary row ── */}
          <div className={styles.summaryRow}>
            <SummaryCard
              icon={CheckCircle2}
              label="Healthy Products"
              value={healthyCnt}
              variant={healthyCnt > 0 ? 'ok' : 'neutral'}
            />
            <SummaryCard
              icon={AlertTriangle}
              label="Warning"
              value={warningCnt}
              variant={warningCnt > 0 ? 'warn' : 'neutral'}
            />
            <SummaryCard
              icon={XCircle}
              label="Critical"
              value={criticalCnt}
              variant={criticalCnt > 0 ? 'bad' : 'neutral'}
            />
            <SummaryCard
              icon={AlertOctagon}
              label="Active Alerts"
              value={activeAlerts.length}
              variant={activeAlerts.length > 0 ? 'bad' : 'neutral'}
            />
          </div>

          {/* ── Active Alerts ── */}
          {activeAlerts.length > 0 && (
            <div className={styles.alertsSection}>
              <div className={styles.sectionHeader}>
                <AlertOctagon size={15} className={styles.alertsIcon} />
                <h2 className={styles.sectionTitle}>Active Alerts</h2>
                <span className={styles.countChip}>{activeAlerts.length}</span>
              </div>
              <div className={styles.alertsList}>
                {activeAlerts.map(a => (
                  <AlertCard key={a.id} alert={a} />
                ))}
              </div>
            </div>
          )}

          {/* ── Replication Status ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Activity size={15} />
              <h2 className={styles.sectionTitle}>Replication Status</h2>
              <span className={styles.countChip}>{filteredReplication.length}</span>
              <div className={styles.filterRow}>
                <Filter size={12} />
                {['ALL', 'HEALTHY', 'WARNING', 'CRITICAL'].map(v => (
                  <button
                    key={v}
                    className={`${styles.filterChip} ${healthFilter === v ? styles.filterChipActive : ''}`}
                    onClick={() => setHealthFilter(v)}
                  >
                    {v === 'ALL' ? 'All' : v}
                  </button>
                ))}
              </div>
            </div>
            {replication.length === 0 ? (
              <div className={styles.emptySection}>No replication data available.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Health</th>
                      <th>Replication Lag</th>
                      <th>Last Sync</th>
                      <th>Last Backup</th>
                      <th>Region</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReplication.map(r => (
                      <tr key={r.id}>
                        <td>
                          <div className={styles.productName}>{r.product_name}</div>
                          <div className={styles.productRpo}>RPO target: {r.target_rpo_hours}h</div>
                        </td>
                        <td>
                          <div className={styles.healthCell}>
                            <HealthDot health={r.health} />
                            <Badge label={r.health} color={HEALTH_COLORS[r.health] ?? '#6B7280'} />
                          </div>
                        </td>
                        <td>
                          <span className={lagClass(r.replication_lag_minutes, r.target_rpo_hours)}>
                            {r.replication_lag_minutes} min
                          </span>
                        </td>
                        <td className={styles.dateCell}>{formatDate(r.last_sync_at)}</td>
                        <td className={styles.dateCell}>{formatDate(r.last_backup_at)}</td>
                        <td>
                          <span className={styles.regionTag}>{r.region || '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Recent Backup Jobs ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Database size={15} />
              <h2 className={styles.sectionTitle}>Recent Backup Jobs</h2>
              <span className={styles.countChip}>{jobs.length}</span>
            </div>
            {jobs.length === 0 ? (
              <div className={styles.emptySection}>No recent backup jobs found.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Started At</th>
                      <th>Size (GB)</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => (
                      <tr key={job.id}>
                        <td className={styles.productName}>{job.product_name}</td>
                        <td>
                          <span className={styles.backupType}>{job.backup_type.replace(/_/g, ' ')}</span>
                        </td>
                        <td>
                          <Badge
                            label={job.status.replace('_', ' ')}
                            color={JOB_STATUS_COLORS[job.status] ?? '#6B7280'}
                          />
                        </td>
                        <td className={styles.dateCell}>{formatDate(job.started_at)}</td>
                        <td>
                          {job.size_gb != null
                            ? <span className={styles.sizeVal}>{job.size_gb.toFixed(1)} GB</span>
                            : <span className={styles.notSet}>—</span>}
                        </td>
                        <td>
                          {job.duration_minutes != null
                            ? <span className={styles.durVal}><Clock size={11} /> {job.duration_minutes} min</span>
                            : <span className={styles.notSet}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

    </div>
  )
}
